import React, { useEffect, useMemo, useState } from 'react';
import { productsAPI } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../common/Toast';
import type { Product } from '../../types/product';
import { useContrastInk } from '../../utils/useContrastInk';

interface BundleMember {
  id: number;
  name: string;
  slug: string;
  priceInt: number;
  currency: string;
  imageUrl: string | null;
}

interface Bundle {
  id: string;
  name: string;
  description?: string;
  bundlePriceInt: number;
  fullPriceInt: number;
  savingsInt: number;
  items: BundleMember[];
}

interface BundleAndSaveProps {
  product: Product;
  className?: string;
  /**
   * Fires with whether this widget will actually render anything.
   *
   * Whether a bundle exists is only known after the fetch resolves, so a
   * parent laying these out cannot tell in advance how many cells it has to
   * fill. The product page uses this to decide whether Frequently Bought
   * Together should span both columns.
   */
  onAvailabilityChange?: (hasBundle: boolean) => void;
}

const fmt = (sen: number, currency: string) =>
  `${currency && currency !== 'MYR' ? currency : 'RM'} ${(sen / 100).toFixed(2)}`;

/**
 * Bundle & Save — the merchant's "buy these together for RM50" offer, shown on
 * the product page of every member product.
 *
 * Ticking every member switches the price to the bundle price; unticking one
 * falls back to the plain sum, because a partial set isn't the offer. Adding
 * puts the individual products in the cart — the discount is then re-derived
 * from the same offer config by the cart and, authoritatively, by checkout.
 * Nothing here can talk the server into a lower price.
 */
export const BundleAndSave: React.FC<BundleAndSaveProps> = ({
  product,
  className = '',
  onAvailabilityChange,
}) => {
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  // Same trap FrequentlyBoughtTogether hit: the panel painted a hardcoded white
  // fallback while its ink came from --card-text, which dark templates set to a
  // light colour. 11 of 23 templates ship a light --card-text; 7 of those have a
  // product page, so this panel rendered near-invisible wherever a merchant on
  // Atelier, Circuit, Depot, Neon, Sugar, Vital or Challenger configured a
  // bundle. Take the page surface, then measure the ink off what actually paints.
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const ink = useContrastInk(panelEl, '#111827');
  const inkMuted = `color-mix(in srgb, ${ink} 65%, transparent)`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await productsAPI.getBundles(product.id);
        if (!alive) return;
        // One offer per product page — the merchant's first matching set.
        // More than one bundle competing for the same shopper reads as noise.
        const first = (res?.bundles || [])[0] || null;
        setBundle(first);
        setSelected(new Set((first?.items || []).map((item) => item.id)));
      } catch {
        // Offers are a bonus, never a blocker — stay silent and render nothing.
        if (alive) setBundle(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [product.id]);

  const members = bundle?.items || [];
  const currency = members[0]?.currency || 'MYR';

  // Same condition as the render guard below — kept next to it so the two
  // cannot drift and tell the parent something the widget then contradicts.
  const willRender = !loading && !!bundle && members.length >= 2;
  useEffect(() => {
    onAvailabilityChange?.(willRender);
  }, [willRender, onAvailabilityChange]);
  const allSelected = members.length > 0 && members.every((m) => selected.has(m.id));

  const selectedSumInt = useMemo(
    () =>
      members.reduce(
        (sum, m) => (selected.has(m.id) ? sum + Number(m.priceInt || 0) : sum),
        0,
      ),
    [members, selected],
  );

  // The offer price only stands for the complete set.
  const payableInt = allSelected ? bundle!.bundlePriceInt : selectedSumInt;
  const savingsInt = allSelected ? bundle!.savingsInt : 0;

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAdd = async () => {
    if (adding || !bundle) return;
    const picks = members.filter((m) => selected.has(m.id));
    if (picks.length === 0) {
      showToast?.('Select at least one item to add to cart.', 'warning');
      return;
    }

    setAdding(true);
    try {
      for (const member of picks) {
        // The real Product for the page we're on; a cart-shaped stand-in for
        // the partners (same shape FrequentlyBoughtTogether builds).
        const asProduct =
          member.id === product.id
            ? product
            : ({
                id: member.id,
                name: member.name,
                slug: member.slug,
                description: '',
                price: member.priceInt / 100,
                priceInt: member.priceInt,
                currency: member.currency,
                productType: (product as any).productType,
                tenantId: (product as any).tenantId,
                status: 'active',
                active: true,
                images: member.imageUrl ? [member.imageUrl] : undefined,
                imageUrl: member.imageUrl || undefined,
              } as unknown as Product);

        addItem(asProduct, 1, undefined, {
          // Groups the lines so the cart can offer "remove the whole set".
          bundleId: bundle.id || bundle.name,
          silent: true,
        });
      }
      showToast?.(
        allSelected
          ? `${bundle.name} added to cart`
          : `${picks.length} item${picks.length === 1 ? '' : 's'} added to cart`,
        'success',
      );
    } catch (err: any) {
      showToast?.(err?.message || 'Failed to add bundle to cart', 'error');
    } finally {
      setAdding(false);
    }
  };

  if (!willRender) return null;

  return (
    <section
      className={`max-w-3xl mx-auto w-full ${className}`}
      aria-labelledby="bundle-heading"
    >
      <div
        ref={setPanelEl}
        className="rounded-3xl p-5 sm:p-6 border"
        style={{
          borderColor: 'color-mix(in srgb, var(--primary-color, #0F766E) 30%, transparent)',
          background: 'var(--card-bg, var(--color-background, #ffffff))',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-1 rounded-full"
            style={{
              background: 'color-mix(in srgb, var(--primary-color, #0F766E) 12%, transparent)',
              color: 'var(--primary-color, #0F766E)',
            }}
          >
            Bundle &amp; Save
          </span>
          {bundle.savingsInt > 0 && (
            <span
              className="text-xs font-semibold"
              style={{ color: 'var(--primary-color, #0F766E)' }}
            >
              Save {fmt(bundle.savingsInt, currency)}
            </span>
          )}
        </div>

        <h2
          id="bundle-heading"
          className="font-bold"
          style={{
            color: ink,
            fontSize: '1.25rem',
            lineHeight: 1.25,
          }}
        >
          {bundle.name}
        </h2>
        {bundle.description && (
          <p
            className="text-xs sm:text-sm mt-1"
            style={{ color: inkMuted }}
          >
            {bundle.description}
          </p>
        )}

        <div className="space-y-2 mt-4">
          {members.map((member) => {
            const isSelected = selected.has(member.id);
            const isThisPage = member.id === product.id;
            return (
              <label
                key={member.id}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-2xl border transition-colors cursor-pointer min-h-[56px]"
                style={{
                  borderColor: isSelected
                    ? 'var(--primary-color, #0F766E)'
                    : 'rgba(0,0,0,0.08)',
                  background: isSelected
                    ? 'color-mix(in srgb, var(--primary-color, #0F766E) 5%, transparent)'
                    : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(member.id)}
                  className="w-5 h-5 rounded accent-[var(--primary-color,#0F766E)] flex-shrink-0 cursor-pointer"
                  aria-label={`Include ${member.name} in ${bundle.name}`}
                />
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden flex-shrink-0"
                  style={{ background: '#f3f4f6' }}
                >
                  {member.imageUrl ? (
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm sm:text-[15px] font-medium truncate"
                    style={{ color: ink }}
                  >
                    {member.name}
                    {isThisPage && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                        style={{
                          background: 'rgba(0,0,0,0.05)',
                          color: inkMuted,
                        }}
                      >
                        This item
                      </span>
                    )}
                  </div>
                  <div
                    className="text-xs sm:text-sm mt-0.5"
                    style={{ color: inkMuted }}
                  >
                    {fmt(member.priceInt, currency)}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div
              className="text-xs uppercase tracking-wider"
              style={{ color: inkMuted }}
            >
              {allSelected ? 'Bundle price' : 'Selected total'}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                className="text-xl sm:text-2xl font-bold"
                style={{ color: ink }}
              >
                {fmt(payableInt, currency)}
              </span>
              {savingsInt > 0 && (
                <>
                  <span
                    className="text-sm line-through"
                    style={{ color: inkMuted }}
                  >
                    {fmt(bundle.fullPriceInt, currency)}
                  </span>
                  <span
                    className="text-xs sm:text-sm font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      background:
                        'color-mix(in srgb, var(--primary-color, #0F766E) 12%, transparent)',
                      color: 'var(--primary-color, #0F766E)',
                    }}
                  >
                    Save {fmt(savingsInt, currency)}
                  </span>
                </>
              )}
            </div>
            {!allSelected && bundle.savingsInt > 0 && (
              <div
                className="text-xs mt-1"
                style={{ color: inkMuted }}
              >
                Add every item to get the bundle price of{' '}
                {fmt(bundle.bundlePriceInt, currency)}.
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={adding || payableInt === 0}
            onClick={handleAdd}
            className="rounded-full px-6 py-3 min-h-[44px] font-semibold text-white transition-transform hover:scale-[0.99] active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--primary-color,#0F766E)]"
            style={{ background: 'var(--primary-color, #0F766E)' }}
          >
            {adding ? 'Adding…' : allSelected ? 'Add bundle to cart' : 'Add selected to cart'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default BundleAndSave;
